import { useEffect, useRef, useState } from "react";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import "./ImageCropDialog.scss";

interface ImageCropDialogProps {
  image: File;
  onCrop: (croppedBlob: Blob) => void;
  onCancel: () => void;
}

export const ImageCropDialog = ({
  image,
  onCrop,
  onCancel,
}: ImageCropDialogProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0, size: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const img = new Image();
    const url = URL.createObjectURL(image);
    img.src = url;
    img.onload = () => {
      setImg(img);
      // Center crop initially
      const size = Math.min(img.width, img.height, 400);
      setCrop({
        x: (img.width - size) / 2,
        y: (img.height - size) / 2,
        size,
      });
      URL.revokeObjectURL(url);
    };
  }, [image]);

  useEffect(() => {
    if (!canvasRef.current || !img) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    // Set canvas size to image size
    canvas.width = img.width;
    canvas.height = img.height;

    // Draw full image
    ctx.drawImage(img, 0, 0);

    // Save context state
    ctx.save();

    // Draw overlay everywhere EXCEPT crop area
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = "destination-out";
    ctx.fillRect(crop.x, crop.y, crop.size, crop.size);

    // Restore context for border
    ctx.restore();

    // Draw crop box border
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2;
    ctx.strokeRect(crop.x, crop.y, crop.size, crop.size);
  }, [img, crop]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    // Check if click is inside crop area
    if (
      x >= crop.x &&
      x <= crop.x + crop.size &&
      y >= crop.y &&
      y <= crop.y + crop.size
    ) {
      setIsDragging(true);
      setDragStart({ x: x - crop.x, y: y - crop.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || !canvasRef.current || !img) {
      return;
    }

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    const newX = Math.max(0, Math.min(x - dragStart.x, img.width - crop.size));
    const newY = Math.max(0, Math.min(y - dragStart.y, img.height - crop.size));

    setCrop({ ...crop, x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleCrop = () => {
    if (!img) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = crop.size;
    canvas.height = crop.size;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    // Draw cropped portion
    ctx.drawImage(
      img,
      crop.x,
      crop.y,
      crop.size,
      crop.size,
      0,
      0,
      crop.size,
      crop.size,
    );

    // Convert to blob
    canvas.toBlob((blob) => {
      if (blob) {
        onCrop(blob);
      }
    }, "image/png");
  };

  const handleZoom = (delta: number) => {
    if (!img) {
      return;
    }

    const newSize = Math.max(
      100,
      Math.min(crop.size + delta, Math.min(img.width, img.height)),
    );
    const centerX = crop.x + crop.size / 2;
    const centerY = crop.y + crop.size / 2;

    setCrop({
      x: Math.max(0, Math.min(centerX - newSize / 2, img.width - newSize)),
      y: Math.max(0, Math.min(centerY - newSize / 2, img.height - newSize)),
      size: newSize,
    });
  };

  return (
    <Dialog onCloseRequest={onCancel} title="Crop Profile Picture">
      <div className="image-crop-dialog">
        <h2>Crop Profile Picture</h2>
        <div className="image-crop-dialog__canvas-container">
          <canvas
            ref={canvasRef}
            className="image-crop-dialog__canvas"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
        </div>
        <div className="image-crop-dialog__controls">
          <button
            onClick={() => handleZoom(-50)}
            className="image-crop-dialog__button"
          >
            Zoom Out
          </button>
          <button
            onClick={() => handleZoom(50)}
            className="image-crop-dialog__button"
          >
            Zoom In
          </button>
        </div>
        <div className="image-crop-dialog__actions">
          <button
            onClick={onCancel}
            className="image-crop-dialog__button image-crop-dialog__button--secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleCrop}
            className="image-crop-dialog__button image-crop-dialog__button--primary"
          >
            Crop & Upload
          </button>
        </div>
      </div>
    </Dialog>
  );
};
